import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { UIUserGroup } from '../model/UiShapes';

interface UserGroupState {
  // State
  userGroups: UIUserGroup[];
  currentUserGroup: UIUserGroup | null;
  selectedUserGroupId: string;
  status: 'idle' | 'loading' | 'failed';

  // Actions
  setUserGroups: (userGroups: UIUserGroup[]) => void;
  setUserGroupsDetails: (userGroups: UIUserGroup[]) => void;
  setCurrentUserGroup: (currentUserGroup: UIUserGroup | null) => void;
  setSelectedUserGroupId: (userGroupId: string) => void;
  changeSelectedUserGroupId: (userGroupId: string) => void;
  addUserGroup: (userGroup: UIUserGroup) => void;
  updateUserGroup: (userGroup: UIUserGroup) => void;
  removeUserGroup: (userGroupId: string) => void;
  setStatus: (status: 'idle' | 'loading' | 'failed') => void;
  clear: () => void;
}

export const useUserGroupStore = create<UserGroupState>()(
  devtools(
    (set, get) => ({
      // Initial state
      userGroups: [],
      currentUserGroup: null,
      selectedUserGroupId: '',
      status: 'idle',

      // Actions
      setUserGroups: (userGroups) => set({ userGroups: Array.isArray(userGroups) ? userGroups : [] }),
      setUserGroupsDetails: (userGroups) => set({ userGroups: Array.isArray(userGroups) ? userGroups : [] }),
      setCurrentUserGroup: (currentUserGroup) => set({ currentUserGroup }),
      setSelectedUserGroupId: (userGroupId) =>
        set((state) => {
          const foundUserGroup = state.userGroups.find((g) => g.guid === userGroupId);

          // 优化：避免不必要的状态更新
          const currentUserGroup = state.currentUserGroup;
          const userGroupChanged =
            (foundUserGroup === null && currentUserGroup !== null) ||
            (foundUserGroup !== null && currentUserGroup === null) ||
            (foundUserGroup && currentUserGroup && foundUserGroup.guid !== currentUserGroup.guid);

          // 只有当userGroup实际发生变化时才更新状态
          if (!userGroupChanged && foundUserGroup && currentUserGroup) {
            // 即使guid相同，也要检查其他关键属性是否变化
            const nameChanged = foundUserGroup.name !== currentUserGroup.name;

            // 如果关键属性有变化，也需要更新
            if (!nameChanged) {
              // 没有实际变化，避免不必要的状态更新
              return {
                selectedUserGroupId: userGroupId
              };
            }
          }

          return {
            selectedUserGroupId: userGroupId,
            currentUserGroup: foundUserGroup || null
          };
        }),
      changeSelectedUserGroupId: (userGroupId) =>
        set((state) => {
          const foundUserGroup = state.userGroups.find((g) => g.guid === userGroupId);
          return {
            selectedUserGroupId: userGroupId,
            currentUserGroup: foundUserGroup || null
          };
        }),
      addUserGroup: (userGroup) =>
        set((state) => ({
          userGroups: [...state.userGroups.filter((g) => g.guid !== userGroup.guid), userGroup]
        })),
      updateUserGroup: (userGroup) =>
        set((state) => ({
          userGroups: state.userGroups.map((g) => (g.guid === userGroup.guid ? userGroup : g)),
          currentUserGroup: state.currentUserGroup?.guid === userGroup.guid ? userGroup : state.currentUserGroup
        })),
      removeUserGroup: (userGroupId) =>
        set((state) => ({
          userGroups: state.userGroups.filter((g) => g.guid !== userGroupId),
          currentUserGroup: state.currentUserGroup?.guid === userGroupId ? null : state.currentUserGroup,
          selectedUserGroupId: state.selectedUserGroupId === userGroupId ? '' : state.selectedUserGroupId
        })),
      setStatus: (status) => set({ status }),
      clear: () =>
        set({
          userGroups: [],
          currentUserGroup: null,
          selectedUserGroupId: '',
          status: 'idle'
        })
    }),
    {
      name: 'user-group-store'
    }
  )
);
